import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Form } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import FicaIndicatorTable, { ficaIndicatorColumns } from './401KFicaIndicatorTable';
import { FicaIndicatorForm } from './Form';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';

import './styles.scoped.css';

const FicaIndicatorWindow = ({ form, setForm, handleViewResults }) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={() => {}}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <FicaIndicatorForm form={form} setForm={setForm} />
      <div className="buttons-row">
        <Button
          variant="primary"
          disabled={!form?.Year}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const FourCeroOneKFica = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ Year: dayjs().year().toString() });
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const fileName = `edge-reporting-401k-fica-indicator-${newDateFormatted.toString()}.csv`;

  const handleViewResults = async () => {
    setLoadingResults(true);
    try {
      const response = await generateReport({
        // reportName '401k-sa-report' is mapped to proc sp_cl_cp_bi_401k_sa (@year input) by
        // the cp-appsync-long-running-report resolver.
        endpoint: '401k-sa-report',
        payload: {
          year: form?.Year,
        },
      });
      if (response?.location) {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);
          setData(reportData);
          setShowResults(true);
        } else {
          // eslint-disable-next-line no-undef
          alert('No data found matching your criteria.');
        }
      } else {
        // eslint-disable-next-line no-undef
        alert(response?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your 401K FICA Indicator Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && '401K FICA Indicator Report'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : '401K FICA Indicator Report'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data, ficaIndicatorColumns)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <FicaIndicatorTable data={data} loading={loadingResults} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
            minHeight: 200,
          }}
          windowContent={<FicaIndicatorWindow form={form} setForm={setForm} handleViewResults={handleViewResults} />}
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default FourCeroOneKFica;
